import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import RenderInput from "../../../shared/components/RenderInput";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faChevronDown,
  faChevronUp,
  faFileExcel,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../utils/apiClient";

export default function UploadedItemCard({
  file,
  sheetList,
  onUpdateSelectedSheet,
  setIsSubmitting,
}) {
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [accountDetails, setAccountDetails] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load company directory from API
  const [companyDirectory, setCompanyDirectory] = useState([]);
  const [companyLoad, setCompanyLoad] = useState({
    loading: true,
    error: null,
  });

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const res = await apiClient.get("/api/v1/machshipAPI/customerList", {
          signal: ac.signal,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }

        const json = await res.json();
        // Accept either an array or an object with a data array
        const rows = Array.isArray(json) ? json : json?.data || [];

        // Normalize to { name, accountCode, ... } and filter valid rows
        const normalized = rows
          .map((r) => ({
            ...r,
            name:
              r.name ??
              r.customerName ??
              (r.displayName
                ? r.displayName.replace(/\s*\([^)]*\)\s*$/, "")
                : undefined),
            accountCode:
              r.accountCode ??
              r.machShipCompanyCode ??
              r.MachshipCompanyCode ??
              r.msfcCode ??
              r.code,
          }))
          .filter((r) => r?.name && r?.accountCode);

        setCompanyDirectory(normalized);
        setCompanyLoad({ loading: false, error: null });
      } catch (e) {
        if (e.name === "AbortError") return;
        setCompanyLoad({
          loading: false,
          error: e.message || "Failed to load companies",
        });
      }
    })();

    return () => ac.abort();
  }, []);

  // Fast lookups for linking
  const companyByName = useMemo(() => {
    const m = new Map();
    companyDirectory.forEach((c) => m.set(c.name, c));
    return m;
  }, [companyDirectory]);

  const companyByCode = useMemo(() => {
    const m = new Map();
    companyDirectory.forEach((c) => m.set(c.accountCode, c));
    return m;
  }, [companyDirectory]);

  // react-select options
  const customerNameOptions = useMemo(
    () =>
      companyDirectory.map((c) => ({
        value: c.name,
        label: `${c.name} (${c.accountCode})`,
      })),
    [companyDirectory]
  );

  const accountCodeOptions = useMemo(
    () =>
      companyDirectory.map((c) => ({
        value: c.accountCode,
        label: `${c.accountCode} — ${c.name}`,
      })),
    [companyDirectory]
  );

  // fields (inject options + disable until loaded)
  const informationInputField = useMemo(
    () => [
      {
        key: "customerName",
        name: "Customer Name",
        type: "searchSelect",
        required: true,
        placeholder: "Search customer...",
        options: customerNameOptions,
        disabled: companyLoad.loading,
        selectProps: {
          creatable: true,
          isClearable: true,
          formatCreateLabel: (input) => `Use: "${input}"`,
          noOptionsMessage: () =>
            companyLoad.loading
              ? "Loading..."
              : companyLoad.error
              ? "Failed to load companies"
              : "No matches",
        },
        tooltip: "Pick a customer; code auto-fills",
      },
      {
        key: "machShipCompanyCode",
        name: "Machship Company Code",
        type: "searchSelect",
        required: true,
        placeholder: "Search account code...",
        options: accountCodeOptions,
        disabled: companyLoad.loading,
        selectProps: {
          creatable: true,
          isClearable: true,
          formatCreateLabel: (input) => `Use: "${input}"`,
          noOptionsMessage: () =>
            companyLoad.loading
              ? "Loading..."
              : companyLoad.error
              ? "Failed to load companies"
              : "No matches",
        },
        tooltip: "Pick a code; customer auto-fills",
      },
    ],
    [customerNameOptions, accountCodeOptions, companyLoad]
  );

  // keep name/code in sync
  const changeValue = (sheetKey, key, value) => {
    setAccountDetails((prev) => {
      const current = prev[sheetKey] || {};
      let next = { ...current, [key]: value };

      if (key === "customerName") {
        const match = companyByName.get(value);
        if (match) {
          next.machShipCompanyCode = match.accountCode;
        }
      }
      if (key === "machShipCompanyCode") {
        const match = companyByCode.get(value);
        if (match) {
          next.customerName = match.name;
        }
      }

      const updated = { ...prev, [sheetKey]: next };
      onUpdateSelectedSheet(selectedSheets, updated);
      return updated;
    });
  };

  const options = sheetList.map((sheet) => ({
    value: sheet,
    label: sheet,
  }));

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": { borderColor: "#9ca3af" },
      padding: "2px",
      borderRadius: "8px",
      minHeight: "42px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#eff6ff",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#1e40af",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6b7280",
      "&:hover": { backgroundColor: "#dc2626", color: "white" },
    }),
  };

  const handleChange = (selectedOptions) => {
    const newSelection = selectedOptions || [];
    const prevSheetNames = new Set(selectedSheets.map((s) => s.value));
    const newSheetNames = new Set(newSelection.map((s) => s.value));

    const addedSheets = [...newSheetNames].filter(
      (s) => !prevSheetNames.has(s)
    );

    setSelectedSheets(newSelection);
    setIsProcessing(true);
    setIsSubmitting(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array", sheetRows: 1 });

      let updatedAccountDetails = { ...accountDetails };

      addedSheets.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) return;

        const A1 = worksheet["A1"]?.v || "";
        const existing = updatedAccountDetails[A1];

        if (existing) {
          if (!existing.sheetNameList.includes(sheetName)) {
            updatedAccountDetails[A1] = {
              ...existing,
              sheetNameList: [...existing.sheetNameList, sheetName],
            };
          }
        } else {
          updatedAccountDetails[A1] = {
            sheetNameList: [sheetName],
            customerName: "",
            machShipCompanyCode: "",
            spesificRequirements: "",
            accountCode: "",
            conPrefix: "",
            controlDocs: "",
            defaultMargin: "",
            margin: "",
            carrierPortal: "",
          };
        }
      });

      for (const [a1Key, details] of Object.entries(updatedAccountDetails)) {
        const filteredSheetList = details.sheetNameList.filter((sheetName) =>
          newSheetNames.has(sheetName)
        );

        if (filteredSheetList.length === 0) {
          delete updatedAccountDetails[a1Key];
        } else if (filteredSheetList.length !== details.sheetNameList.length) {
          updatedAccountDetails[a1Key] = {
            ...details,
            sheetNameList: filteredSheetList,
          };
        }
      }

      setAccountDetails(updatedAccountDetails);
      onUpdateSelectedSheet(newSelection, updatedAccountDetails);
      setIsProcessing(false);
      setIsSubmitting(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const getFileIcon = () => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "xlsx" || extension === "xls" || extension === "xlsm") {
      return faFileExcel;
    }
    return faFile;
  };

  const getFileSize = () => {
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    return sizeInMB < 1
      ? `${(file.size / 1024).toFixed(0)} KB`
      : `${sizeInMB} MB`;
  };

  const accountDetailsCount = Object.keys(accountDetails).length;
  const hasAccountDetails = accountDetailsCount > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <FontAwesomeIcon
                icon={getFileIcon()}
                className="w-5 h-5 text-blue-600"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="text-sm font-medium text-gray-900 truncate"
                title={file.name}
              >
                {file.name}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {getFileSize()} • {sheetList.length} sheet
                {sheetList.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {hasAccountDetails && (
              <div className="hidden sm:flex items-center space-x-2">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  {accountDetailsCount} account
                  {accountDetailsCount !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-lg hover:bg-white/80 transition-colors"
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="w-4 h-4 text-gray-500"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Sheet Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">
                Select Sheets
              </label>
              {isProcessing && (
                <div className="flex items-center text-xs text-blue-600">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin w-3 h-3 mr-1"
                  />
                  Processing...
                </div>
              )}
            </div>

            <Select
              options={options}
              isMulti
              blurInputOnSelect={false}
              closeMenuOnSelect={false}
              onChange={handleChange}
              value={selectedSheets}
              placeholder="Select one or more sheets..."
              styles={customSelectStyles}
              className="text-sm"
              isDisabled={isProcessing}
            />

            {selectedSheets.length > 0 && (
              <p className="text-xs text-gray-600">
                {selectedSheets.length} sheet
                {selectedSheets.length !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          {/* Account Details */}
          {hasAccountDetails && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  Account Details ({accountDetailsCount})
                </h4>
                <div className="sm:hidden">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                    {accountDetailsCount} account
                    {accountDetailsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {Object.entries(accountDetails).map(([a1Key, detail], index) => (
                <div
                  key={a1Key}
                  className="border border-gray-200 rounded-lg bg-gray-50/50"
                >
                  {/* Account Header */}
                  <div className="bg-white px-4 py-3 border-b border-gray-100 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-gray-800 flex items-center">
                        <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium mr-2">
                          {index + 1}
                        </span>
                        {a1Key || `Account ${index + 1}`}
                      </h5>
                      <div className="text-xs text-gray-500">
                        {detail.sheetNameList?.length || 0} sheet
                        {(detail.sheetNameList?.length || 0) !== 1 ? "s" : ""}
                      </div>
                    </div>

                    {detail.sheetNameList &&
                      detail.sheetNameList.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {detail.sheetNameList.map((sheetName, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700"
                            >
                              {sheetName}
                            </span>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Form Fields */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {informationInputField.map((inputField, fieldIndex) => (
                        <div
                          key={fieldIndex}
                          className={`${
                            inputField.type === "textarea"
                              ? "sm:col-span-2 lg:col-span-3"
                              : ""
                          }`}
                        >
                          <RenderInput
                            input={inputField}
                            changeValue={(key, value) =>
                              changeValue(a1Key, key, value)
                            }
                            value={
                              accountDetails[a1Key]?.[inputField.key] || ""
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {selectedSheets.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FontAwesomeIcon
                  icon={faFile}
                  className="w-6 h-6 text-gray-400"
                />
              </div>
              <p className="text-sm text-gray-500">
                Select sheets to configure account details
              </p>
            </div>
          )}

          {/* API load state */}
          {companyLoad.error && (
            <p className="text-xs text-red-600">
              Failed to load company directory: {companyLoad.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
